import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import {
  sourcesGetCalendars,
  sourcesGetGmailQueries,
  sourcesListUserSources,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { GmailQuery, Source, SourceCalendar } from 'api/model'

function useFetchSources() {
  const [activeOrganization] = useActiveOrganization()
  const [sources, setSources] = useState<Source[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await sourcesListUserSources(activeOrganization)
        setSources(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setSources])

  return {
    isLoading,
    sources,
    error,
  }
}

function useFetchGmailQueries(sourceId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [gmailQueries, setGmailQueries] = useState<GmailQuery[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchGmailQueries = useCallback(async () => {
    if (!activeOrganization || !sourceId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await sourcesGetGmailQueries(activeOrganization, sourceId)
      setGmailQueries(response.data)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, setGmailQueries, sourceId])

  useLayoutEffect(() => {
    refetchGmailQueries()
  }, [refetchGmailQueries])

  return {
    isLoading,
    gmailQueries,
    error,
    setGmailQueries,
    refetchGmailQueries,
  }
}

function useFetchSourceCalendars() {
  const [activeOrganization] = useActiveOrganization()
  const [sourceCalendars, setSourceCalendars] = useState<SourceCalendar[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await sourcesGetCalendars(activeOrganization)
        setSourceCalendars(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setSourceCalendars])

  return {
    isLoading,
    sourceCalendars,
    error,
    setSourceCalendars,
  }
}

export { useFetchGmailQueries, useFetchSources, useFetchSourceCalendars }
